'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { 
  initiateGoogleAuth, 
  initiateMicrosoftAuth,
  extractTokenFromUrl, 
  authenticateWithOAuth,
  storeAuthData,
  handleMicrosoftRedirectResponse,
  initiateMicrosoftAuthPopup,
  switchMicrosoftAccount,
  signOut
} from '@/services/api/auth'
import type { AuthProvider } from '@/services/api/auth'
import { useAuth } from '@/context/AuthContext'

// Create a separate client-only component for the particles
function AnimatedParticles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {Array.from({ length: 30 }).map((_, i) => (
        <div 
          key={i}
          className="absolute rounded-full bg-blue-500/10 animate-float"
          style={{
            width: `${Math.random() * 20 + 5}px`,
            height: `${Math.random() * 20 + 5}px`,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animationDuration: `${Math.random() * 10 + 10}s`,
            animationDelay: `${Math.random() * 5}s`,
            opacity: Math.random() * 0.5,
          }}
        ></div>
      ))}
    </div>
  )
}

export default function LoginPage() {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [signingMethod, setSigningMethod] = useState<string | null>(null)
  const [authError, setAuthError] = useState<string | null>(null)
  const [infoMessage, setInfoMessage] = useState<string | null>(null)
  const router = useRouter()
  
  // Only show particles after the component has mounted on the client
  const [showParticles, setShowParticles] = useState(false)
  
  const { isAuthenticated, checkAuthStatus } = useAuth()

  // Helper function to get redirect destination
  const getRedirectDestination = () => {
    const intendedDestination = localStorage.getItem('intended_destination')
    if (intendedDestination && intendedDestination !== '/login') {
      localStorage.removeItem('intended_destination') // Clean up
      return intendedDestination
    }
    return '/dashboard'
  }

  // Helper function to perform redirect
  const performRedirect = (destination: string) => {
    router.push(destination)
    
    // Fallback direct navigation if router.push doesn't work
    setTimeout(() => {
      if (window.location.pathname !== destination) {
        window.location.href = destination
      }
    }, 1000)
  }
  
  useEffect(() => {
    setIsLoaded(true)
    setShowParticles(true)
    
    // Add a subtle animation effect to the page elements
    const timer = setTimeout(() => {
      document.querySelectorAll('.stagger-animation').forEach((element, index) => {
        (element as HTMLElement).style.opacity = '1';
        (element as HTMLElement).style.transform = 'translateY(0)';
      });
    }, 100);
    
    return () => clearTimeout(timer);
  }, [])

  // Updated token extraction to handle Google OAuth redirects only (Microsoft now uses popup)
  useEffect(() => {
    const checkForRedirect = async () => {
      try {

        
        // Check if we're already processing authentication
        const isProcessing = sessionStorage.getItem('auth_processing')
        if (isProcessing === 'true') {

          return
        }
        
        // Check for Google tokens in URL hash (fragment)
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const idToken = hashParams.get('id_token')
        const googleAccessToken = hashParams.get('access_token')


        
        // Handle Google implicit flow
        if (idToken || googleAccessToken) {

          setIsSigningIn(true)
          setSigningMethod('google')
          
          // Set processing flag to prevent duplicate calls
          sessionStorage.setItem('auth_processing', 'true')
          
          try {
            // Use ID token if available, otherwise use access token
            const tokenToUse = idToken || googleAccessToken
            
            if (tokenToUse) {

              
              // Send to backend
              const response = await authenticateWithOAuth('google' as AuthProvider, tokenToUse)
              

              
              // Store authentication data
              storeAuthData(response)
              
              // Clear processing flag
              sessionStorage.removeItem('auth_processing')
              
              // Trigger auth context to reload immediately
              await checkAuthStatus()
              
              // Get the intended destination and redirect
              const destination = getRedirectDestination()
              
              // Add a delay before redirecting to ensure everything is saved
              setTimeout(() => {
                performRedirect(destination)
              }, 500)
              
            } else {
              throw new Error('No valid token found in URL')
            }
          } catch (error: any) {
            console.error('Google authentication error:', error)
            
            // Clear processing flag on error
            sessionStorage.removeItem('auth_processing')
            
            // Check if it's an API error with a specific message
            if (error?.message) {
              setAuthError(`Google authentication failed: ${error.message}`)
            } else {
              console.error('No valid token found')
              setAuthError(`No authentication token received from Google`)
            }
            
            setIsSigningIn(false)
          }
        }
        
      } catch (error) {
        console.error('Error in OAuth redirect handling:', error)
        setAuthError('An unexpected error occurred during authentication')
        setIsSigningIn(false)
        
        // Clear processing flag on error
        sessionStorage.removeItem('auth_processing')
      }
    }
    
    checkForRedirect()
  }, [router, checkAuthStatus])

  // Add this at the top level of your component, right after the component declaration
  useEffect(() => {
    if (isAuthenticated) {
      const destination = getRedirectDestination()
      performRedirect(destination)
    }
  }, [isAuthenticated, router]);

  const handleGoogleSignIn = async () => {
    try {
      setIsSigningIn(true);
      setSigningMethod('google');
      setAuthError(null);
      setInfoMessage(null);
      
      // Check if user is already authenticated and wants to switch accounts
      if (isAuthenticated) {

        setInfoMessage('Switching to a different Google account...')
        
        // For Google, we need to sign out first and then redirect to auth
        // This ensures the user gets the account selection screen
        signOut() // Clear current authentication
        
        // Small delay to ensure sign out completes
        setTimeout(async () => {
          try {
            // Store the provider type for use after redirect
            localStorage.setItem('auth_provider', 'google');
            
            // Get the Google auth URL and redirect the user
            const authUrl = await initiateGoogleAuth();
            
            // Navigate to the auth URL
            window.location.href = authUrl;
          } catch (error) {
            console.error('Error initiating Google auth after sign out:', error)
            setAuthError('Failed to switch Google account. Please try again.')
            setIsSigningIn(false)
            setInfoMessage(null)
          }
        }, 500)
        
        return // Exit early since we're redirecting
      }
      

      
      // Store the provider type for use after redirect
      localStorage.setItem('auth_provider', 'google');
      
      // Get the Google auth URL and redirect the user
      const authUrl = await initiateGoogleAuth();
      
      // Navigate to the auth URL
      window.location.href = authUrl;
      
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      setIsSigningIn(false);
      setInfoMessage(null);
      
      // Handle different types of errors with user-friendly messages
      if (error.message?.includes('cancelled') || error.message?.includes('popup_closed_by_user')) {
        // User cancelled the authentication - show a brief info message

        setAuthError(null)
        setInfoMessage('Sign-in cancelled. You can try again anytime.')
        setTimeout(() => setInfoMessage(null), 3000)
        return
      } else if (error.message?.includes('access_denied')) {
        setAuthError('Access denied. Please grant permission to access your Google account.')
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        setAuthError('Network error occurred. Please check your internet connection and try again.')
      } else if (error.message?.includes('timeout')) {
        setAuthError('Authentication timed out. Please try again.')
      } else if (error.message?.includes('not configured')) {
        setAuthError('Google authentication is not properly configured. Please contact support.')
      } else if (error.message) {
        setAuthError(`Google sign-in failed: ${error.message}`)
      } else {
        setAuthError('Failed to initialize Google sign-in. Please try again.')
      }
    }
  }
  
  const handleMicrosoftSignIn = async () => {
    try {

      setIsSigningIn(true);
      setSigningMethod('microsoft');
      setAuthError(null);
      setInfoMessage(null);
      
      let accessToken: string;
      
      // Check if user is already authenticated
      if (isAuthenticated) {

        setInfoMessage('Switching to a different Microsoft account...')
        
        // Use the account switching function which clears cache first
        accessToken = await switchMicrosoftAccount();

      } else {

        // Use regular popup-based authentication
        accessToken = await initiateMicrosoftAuthPopup();

      }
      

      
      // Send the access token to your backend
      const response = await authenticateWithOAuth('microsoft' as AuthProvider, accessToken)
      
      // Store authentication data
      storeAuthData(response)
      
      // Trigger auth context to reload immediately
      await checkAuthStatus()
      
      // Clear any info messages
      setInfoMessage(null)
      
      // Get the intended destination and redirect
      const destination = getRedirectDestination()
      
      // Add a delay before redirecting to ensure everything is saved
      setTimeout(() => {
        performRedirect(destination)
      }, 500)
      
    } catch (error: any) {
      console.error('Microsoft sign-in error:', error);
      setIsSigningIn(false);
      setInfoMessage(null);
      
      // Handle different types of errors with user-friendly messages
      if (error.message?.includes('cancelled') || error.message?.includes('User cancelled')) {
        // User cancelled the authentication - show a brief info message

        setAuthError(null) // Clear any existing errors
        setInfoMessage('Sign-in cancelled. You can try again anytime.')
        setTimeout(() => setInfoMessage(null), 3000) // Clear message after 3 seconds
        return
      } else if (error.message?.includes('popup_closed_by_user')) {
        // User closed the popup - also normal behavior

        setAuthError(null)
        setInfoMessage('Sign-in cancelled. You can try again anytime.')
        setTimeout(() => setInfoMessage(null), 3000)
        return
      } else if (error.message?.includes('consent_required')) {
        setAuthError('Additional permissions are required. Please try signing in again and grant the necessary permissions.')
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        setAuthError('Network error occurred. Please check your internet connection and try again.')
      } else if (error.message?.includes('timeout')) {
        setAuthError('Authentication timed out. Please try again.')
      } else if (error.message) {
        // Show the specific error message from the backend or auth service
        setAuthError(`Authentication failed: ${error.message}`)
      } else {
        // Generic fallback error
        setAuthError('Failed to authenticate with Microsoft. Please try again.')
      }
    }
  }
  
  return (
    <div className="bg-[#050510] overflow-hidden flex justify-center items-center py-24 flex-1">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('/images/grid-pattern.png')] bg-repeat opacity-5"></div>
      
      {/* Animated particles background - only rendered client-side */}
      {showParticles && <AnimatedParticles />}
      
      {/* Animated radial gradient */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-gradient-radial from-blue-500/10 to-transparent rounded-full opacity-50 blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-1/3 h-1/3 bg-gradient-radial from-cyan-500/10 to-transparent rounded-full opacity-40 blur-3xl"></div>
      </div>
      
      {/* Animated glow spots */}
      <div className="absolute top-20 -left-40 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-slow-pulse" 
           style={{ background: 'linear-gradient(90deg, #0c4a6e, #0284c7)' }}></div>
      
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-slow-pulse-delay" 
           style={{ background: 'linear-gradient(90deg, #0369a1, #0ea5e9)' }}></div>

      {/* Main content */}
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col items-center">
          {/* Logo and branding */}
          <div className={`mb-8 transform transition-all duration-1000 stagger-animation opacity-0 translate-y-12`} 
               style={{ transitionDelay: '100ms' }}>
            <Image 
              src="/logo.png" 
              alt="AI Learning Platform" 
              width={100} 
              height={100} 
              className="mx-auto mb-6"
              priority
            />
            <h1 className="text-3xl md:text-5xl font-bold text-center">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                AI
              </span>
              <span className="text-white"> Learning Platform</span>
            </h1>
          </div>
          
          {/* Login card with perspective effect */}
          <div 
            className={`w-full max-w-md perspective-1000 transition-all duration-1000 stagger-animation opacity-0 translate-y-12`}
            style={{ transitionDelay: '300ms' }}
          >
            <div className="relative">
              {/* Card container with 3D hover effect */}
              <div className="transform-style-3d transition-transform duration-500 hover:rotate-y-2 hover:rotate-x-2">
                {/* Main card */}
                <div className="bg-[#0c0c1d]/80 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl shadow-blue-900/10">
                  {/* Glass effect top bar */}
                  <div className="h-2 w-full bg-gradient-to-r from-cyan-500 to-blue-500"></div>
                  
                  <div className="p-8 md:p-10">
                    {/* Welcome message */}
                    <div className="text-center mb-8">
                      <div className="inline-flex items-center bg-white/5 backdrop-blur-sm border border-white/10 rounded-full py-2 px-4 mb-6">
                        <span className="inline-block w-2 h-2 rounded-full bg-cyan-400 mr-2 animate-pulse"></span>
                        <span className="text-white/90 text-sm font-medium">Secure Sign In</span>
                      </div>
                      
                      <h2 className="text-2xl font-bold text-white mb-3">Welcome Back!</h2>
                      <p className="text-white/60">
                        Let's boost and change our education together!
                      </p>
                    </div>
                    
                    {/* Error message if auth fails */}
                    {authError && (
                      <div className="mb-6 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                        <p className="text-red-400 text-sm text-center">{authError}</p>
                      </div>
                    )}
                    
                    {/* Info message for user actions like cancellation */}
                    {infoMessage && (
                      <div className="mb-6 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                        <p className="text-blue-400 text-sm text-center">{infoMessage}</p>
                      </div>
                    )}
                    
                    {/* Social sign-in options */}
                    <div className="space-y-5">
                      <button
                        onClick={handleGoogleSignIn}
                        disabled={isSigningIn}
                        className="group w-full bg-white hover:bg-gray-100 text-gray-800 font-medium py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center disabled:opacity-70 relative overflow-hidden shadow-lg hover:shadow-xl"
                      >
                        <div className="absolute inset-0 w-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-80 pointer-events-none group-hover:w-full transition-all duration-700"></div>
                        <div className="relative flex items-center justify-center">
                          {isSigningIn && signingMethod === 'google' ? (
                            <>
                              <svg className="animate-spin mr-3 h-5 w-5 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>{isAuthenticated ? 'Switching Google account...' : 'Signing in with Google...'}</span>
                            </>
                          ) : (
                            <>
                              <Image src="/images/google-logo.png" alt="Google" width={24} height={24} className="mr-4" priority />
                              <span className="group-hover:text-white transition-colors duration-300">
                                {isAuthenticated ? 'Switch Google Account' : 'Sign in with Google'}
                              </span>
                            </>
                          )}
                        </div>
                      </button>
                      
                      <button
                        onClick={handleMicrosoftSignIn}
                        disabled={isSigningIn}
                        className="group w-full bg-[#0078D4] hover:bg-[#106EBE] text-white font-medium py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center disabled:opacity-70 relative overflow-hidden shadow-lg hover:shadow-xl"
                      >
                        <div className="absolute inset-0 w-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-80 pointer-events-none group-hover:w-full transition-all duration-700"></div>
                        <div className="relative flex items-center justify-center">
                          {isSigningIn && signingMethod === 'microsoft' ? (
                            <>
                              <svg className="animate-spin mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>{isAuthenticated ? 'Switching Microsoft account...' : 'Signing in with Microsoft...'}</span>
                            </>
                          ) : (
                            <>
                              <Image src="/images/microsoft-logo.png" alt="Microsoft" width={24} height={24} className="mr-4 flex-shrink-0" />
                              <span className="group-hover:text-white transition-colors duration-300">
                                {isAuthenticated ? 'Switch Microsoft Account' : 'Sign in with Outlook'}
                              </span>
                            </>
                          )}
                        </div>
                      </button>
                    </div>
                    
                    {/* Security badge */}
                    <div className="mt-8 pt-6 border-t border-white/5 text-center">
                      <p className="text-white/40 text-xs flex items-center justify-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                        </svg>
                        Secured with end-to-end encryption
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full blur-lg"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-tr from-blue-500/20 to-purple-500/20 rounded-full blur-xl"></div>
            </div>
          </div>
          
          {/* Feature highlights - Made more compact to fit better */}
          <div 
            className={`mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-center max-w-3xl mx-auto transition-all duration-1000 stagger-animation opacity-0 translate-y-12`}
            style={{ transitionDelay: '500ms' }}
          >
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all hover:scale-105 transform">
              <div className="text-cyan-400 mb-2">
                <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 className="text-white font-medium text-sm">Personalized Learning</h3>
              <p className="text-white/50 text-xs mt-1">Tailored to your needs</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all hover:scale-105 transform">
              <div className="text-cyan-400 mb-2">
                <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                </svg>
              </div>
              <h3 className="text-white font-medium text-sm">AI-Powered Feedback</h3>
              <p className="text-white/50 text-xs mt-1">Real-time assistance</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all hover:scale-105 transform">
              <div className="text-cyan-400 mb-2">
                <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
              </div>
              <h3 className="text-white font-medium text-sm">Progress Tracking</h3>
              <p className="text-white/50 text-xs mt-1">Monitor your journey</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 